// React state layer for tournaments. The API server (backed by PostgreSQL) is
// the single source of truth; this provider holds the list of tournament
// summaries plus the currently-opened tournament snapshot, and mutates them
// through `apiClient`. Components read it via the `useTournament` hook.
//
// There is no client-side cascade logic — creating, scoring, and advancing all
// round-trip to the server, which runs the standings/slot/bracket cascades and
// returns the fresh state. Mutations that change a summary field (status,
// player count, existence) also refresh the list.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  Company,
  CompanySeatUsage,
  CompanyType,
  FootballMatchEvent,
  GameScore,
  Location,
  PlayerInput,
  TournamentState,
  TournamentSummary,
} from '../domain';
import * as api from '../data/apiClient';
import type { CompanyProfileRequest, CreateRequest } from '../data/apiClient';

export type LoadStatus = 'loading' | 'ready' | 'error';

export interface TournamentContextValue {
  summaries: TournamentSummary[];
  /** Every company on the platform (oldest first; the default company leads). */
  companies: Company[];
  /**
   * Subscription seat usage per accessible company — the athlete/coach counts a
   * plan is measured against (see `features/subscription-seats.md`). Loaded with
   * the company list so the companies screen can show a figure per row without a
   * request each; a company absent from the list simply has no seats yet
   * (`findSeatUsage` reads that as zero). A surface showing one club fetches its
   * own live figure instead — see `fetchCompanySeatUsage`.
   */
  companySeats: CompanySeatUsage[];
  /** The location dictionary, ordered by canonical (English) name. */
  locations: Location[];
  selectedId: string | null;
  tournament: TournamentState | null;
  status: LoadStatus;
  error: string | null;
  refreshList: () => Promise<void>;
  select: (id: string) => Promise<void>;
  close: () => void;
  /**
   * Bumped whenever the open tournament's public sign-up queue changes on the
   * server (a verification, approval or rejection — possibly from another tab).
   * Views that fetch registrations separately watch this to know when to refetch.
   */
  registrationsVersion: number;
  create: (req: CreateRequest) => Promise<void>;
  createCompany: (name: string, type: CompanyType, locationId: number) => Promise<void>;
  renameCompany: (id: number, name: string) => Promise<void>;
  updateCompanyProfile: (id: number, req: CompanyProfileRequest) => Promise<void>;
  removeCompany: (id: number) => Promise<void>;
  /** Mark a self-registered company as verified (VERIFY_COMPANY holders). */
  verifyCompany: (id: number) => Promise<void>;
  addPlayer: (input: PlayerInput) => Promise<void>;
  removePlayer: (playerId: string) => Promise<void>;
  /** Edit a DRAFT roster entry (rename, or revise a team's member list). */
  updatePlayer: (
    playerId: string,
    update: { name?: string; members?: string[] },
  ) => Promise<void>;
  withdrawPlayer: (playerId: string) => Promise<void>;
  /** Add a referee to the open (football) tournament. */
  addReferee: (name: string) => Promise<void>;
  /** Remove a referee, clearing any match assignments they held. */
  removeReferee: (refereeId: string) => Promise<void>;
  /** Assign a referee to a match (cell or node), or clear with null. */
  assignReferee: (matchId: string, refereeId: string | null) => Promise<void>;
  start: (preferLargerOnRemainderTwo?: boolean) => Promise<void>;
  finish: () => Promise<void>;
  remove: (id?: string) => Promise<void>;
  approveRegistration: (registrationId: string) => Promise<void>;
  rejectRegistration: (registrationId: string) => Promise<void>;
  /**
   * Record a match score; `final` marks a football score as the final result
   * and `events` replaces the match's recorded goal/card events (omitted =
   * keep the stored list; both football-only).
   */
  updateCell: (
    cellId: string,
    games: GameScore[],
    final?: boolean,
    events?: FootballMatchEvent[],
  ) => Promise<void>;
  updateNode: (
    nodeId: string,
    games: GameScore[],
    final?: boolean,
    events?: FootballMatchEvent[],
  ) => Promise<void>;
}

const TournamentContext = createContext<TournamentContextValue | null>(null);

export function TournamentProvider({ children }: { children: ReactNode }) {
  const [summaries, setSummaries] = useState<TournamentSummary[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companySeats, setCompanySeats] = useState<CompanySeatUsage[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tournament, setTournament] = useState<TournamentState | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [registrationsVersion, setRegistrationsVersion] = useState(0);

  // Latest tournament, kept in a ref so the SSE handler can compare against it
  // (e.g. to detect a status change) without re-subscribing on every snapshot.
  const tournamentRef = useRef(tournament);
  useEffect(() => {
    tournamentRef.current = tournament;
  }, [tournament]);

  // Wraps an API operation so a single place handles error/status transitions.
  const run = useCallback(async <T,>(op: () => Promise<T>): Promise<T> => {
    try {
      setError(null);
      const result = await op();
      setStatus('ready');
      return result;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
      setStatus('error');
      throw e;
    }
  }, []);

  // Internal (unwrapped) list refresh, reused by mutating actions.
  const loadSummaries = useCallback(async () => {
    setSummaries(await api.fetchTournaments());
  }, []);

  const loadCompanies = useCallback(async () => {
    setCompanies(await api.fetchCompanies());
  }, []);

  // Seat usage is one aggregate for every accessible company, so it rides along
  // with the company list rather than costing a request per row. An account with
  // no companies (an athlete's own login) gets an empty list, not an error.
  const loadCompanySeats = useCallback(async () => {
    setCompanySeats(await api.fetchCompanySeats());
  }, []);

  // The location dictionary is small and static, so it is loaded once with the
  // list and reused by the company-create form and the profile display.
  const loadLocations = useCallback(async () => {
    setLocations(await api.fetchLocations());
  }, []);

  const refreshList = useCallback(
    () =>
      run(async () => {
        await Promise.all([
          loadSummaries(),
          loadCompanies(),
          loadCompanySeats(),
          loadLocations(),
        ]);
      }),
    [run, loadSummaries, loadCompanies, loadCompanySeats, loadLocations],
  );

  const select = useCallback(
    (id: string) =>
      run(async () => {
        const next = await api.fetchTournament(id);
        setSelectedId(id);
        setTournament(next);
      }),
    [run],
  );

  const close = useCallback(() => {
    setSelectedId(null);
    setTournament(null);
  }, []);

  const create = useCallback(
    (req: CreateRequest) =>
      run(async () => {
        const next = await api.createTournament(req);
        if (next) {
          setSelectedId(next.id);
          setTournament(next);
        }
        await loadSummaries();
      }),
    [run, loadSummaries],
  );

  const createCompany = useCallback(
    (name: string, type: CompanyType, locationId: number) =>
      run(async () => {
        await api.createCompany(name, type, locationId);
        await loadCompanies();
      }),
    [run, loadCompanies],
  );

  const renameCompany = useCallback(
    (id: number, name: string) =>
      run(async () => {
        await api.renameCompany(id, name);
        await loadCompanies();
      }),
    [run, loadCompanies],
  );

  const updateCompanyProfile = useCallback(
    (id: number, req: CompanyProfileRequest) =>
      run(async () => {
        await api.updateCompanyProfile(id, req);
        await loadCompanies();
      }),
    [run, loadCompanies],
  );

  const removeCompany = useCallback(
    (id: number) =>
      run(async () => {
        setCompanies(await api.deleteCompany(id));
      }),
    [run],
  );

  const verifyCompany = useCallback(
    (id: number) =>
      run(async () => {
        await api.verifyCompany(id);
        await loadCompanies();
      }),
    [run, loadCompanies],
  );

  // Applies a mutation against the selected tournament, then refreshes the list
  // since status / player counts shown there may have changed.
  const mutateSelected = useCallback(
    (op: (id: string) => Promise<TournamentState | null>) =>
      run(async () => {
        if (!selectedId) throw new Error('No tournament selected.');
        const next = await op(selectedId);
        setTournament(next);
        await loadSummaries();
      }),
    [run, selectedId, loadSummaries],
  );

  const addPlayer = useCallback(
    (input: PlayerInput) => mutateSelected((id) => api.addPlayer(id, input)),
    [mutateSelected],
  );
  const removePlayer = useCallback(
    (playerId: string) => mutateSelected((id) => api.removePlayer(id, playerId)),
    [mutateSelected],
  );
  const updatePlayer = useCallback(
    (playerId: string, update: { name?: string; members?: string[] }) =>
      mutateSelected((id) => api.updatePlayer(id, playerId, update)),
    [mutateSelected],
  );
  const withdrawPlayer = useCallback(
    (playerId: string) => mutateSelected((id) => api.withdrawPlayer(id, playerId)),
    [mutateSelected],
  );
  const addReferee = useCallback(
    (name: string) => mutateSelected((id) => api.addReferee(id, name)),
    [mutateSelected],
  );
  const removeReferee = useCallback(
    (refereeId: string) => mutateSelected((id) => api.removeReferee(id, refereeId)),
    [mutateSelected],
  );
  const assignReferee = useCallback(
    (matchId: string, refereeId: string | null) =>
      mutateSelected((id) => api.assignReferee(id, matchId, refereeId)),
    [mutateSelected],
  );
  const start = useCallback(
    (preferLargerOnRemainderTwo?: boolean) =>
      mutateSelected((id) => api.startTournament(id, preferLargerOnRemainderTwo)),
    [mutateSelected],
  );
  const finish = useCallback(
    () => mutateSelected((id) => api.finishTournament(id)),
    [mutateSelected],
  );
  const approveRegistration = useCallback(
    (registrationId: string) =>
      mutateSelected((id) => api.approveRegistration(id, registrationId)),
    [mutateSelected],
  );
  const rejectRegistration = useCallback(
    (registrationId: string) =>
      mutateSelected((id) => api.rejectRegistration(id, registrationId)),
    [mutateSelected],
  );
  const updateCell = useCallback(
    (
      cellId: string,
      games: GameScore[],
      final?: boolean,
      events?: FootballMatchEvent[],
    ) => mutateSelected((id) => api.updateCell(id, cellId, games, final, events)),
    [mutateSelected],
  );
  const updateNode = useCallback(
    (
      nodeId: string,
      games: GameScore[],
      final?: boolean,
      events?: FootballMatchEvent[],
    ) => mutateSelected((id) => api.updateNode(id, nodeId, games, final, events)),
    [mutateSelected],
  );

  const remove = useCallback(
    (id?: string) =>
      run(async () => {
        const target = id ?? selectedId;
        if (!target) throw new Error('No tournament to delete.');
        await api.deleteTournament(target);
        if (target === selectedId) {
          setSelectedId(null);
          setTournament(null);
        }
        await loadSummaries();
      }),
    [run, selectedId, loadSummaries],
  );

  // Load the list once on mount.
  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  // Live cross-tab sync: while a tournament is open, subscribe to its Server-Sent
  // Events stream so changes made elsewhere (another tab, or a colleague sharing
  // the login) appear here immediately. The server pushes the full snapshot on
  // every mutation; sign-up queue changes and deletions arrive as their own
  // events. The subscription authenticates via an `Authorization` header (not a
  // URL token) and reconnects with backoff on drops (expected on Cloud Run, which
  // caps request duration).
  useEffect(() => {
    if (!selectedId) return;

    const unsubscribe = api.subscribeTournamentEvents(selectedId, {
      // On (re)connect, reconcile the current snapshot: closes the gap between
      // select()'s initial fetch and the stream opening, and re-syncs any events
      // missed while the stream was briefly disconnected. Errors are ignored
      // (a 401 is handled by the API layer's logout).
      onOpen: () => {
        void api
          .fetchTournament(selectedId)
          .then((snap) => {
            if (snap && snap.id === selectedId) {
              setTournament(snap);
              setStatus('ready');
            }
          })
          .catch(() => {});
      },
      onState: (next) => {
        // Guard against a late frame from a previously-selected tournament.
        if (next.id !== selectedId) return;
        const statusChanged = tournamentRef.current?.status !== next.status;
        setTournament(next);
        setStatus('ready');
        // A status transition (e.g. another tab started or finished the
        // tournament) changes what the summary list shows, so refresh it.
        if (statusChanged) void loadSummaries();
      },
      onRegistrations: () => setRegistrationsVersion((v) => v + 1),
      onDeleted: () => {
        // Another tab deleted this tournament; drop back to the list.
        setSelectedId(null);
        setTournament(null);
        void loadSummaries();
      },
    });

    return unsubscribe;
  }, [selectedId, loadSummaries]);

  const value = useMemo<TournamentContextValue>(
    () => ({
      summaries,
      companies,
      companySeats,
      locations,
      selectedId,
      tournament,
      status,
      error,
      refreshList,
      select,
      close,
      registrationsVersion,
      create,
      createCompany,
      renameCompany,
      updateCompanyProfile,
      removeCompany,
      verifyCompany,
      addPlayer,
      removePlayer,
      updatePlayer,
      withdrawPlayer,
      addReferee,
      removeReferee,
      assignReferee,
      start,
      finish,
      remove,
      approveRegistration,
      rejectRegistration,
      updateCell,
      updateNode,
    }),
    [
      summaries,
      companies,
      companySeats,
      locations,
      selectedId,
      tournament,
      status,
      error,
      refreshList,
      select,
      close,
      registrationsVersion,
      create,
      createCompany,
      renameCompany,
      updateCompanyProfile,
      removeCompany,
      verifyCompany,
      addPlayer,
      removePlayer,
      updatePlayer,
      withdrawPlayer,
      addReferee,
      removeReferee,
      assignReferee,
      start,
      finish,
      remove,
      approveRegistration,
      rejectRegistration,
      updateCell,
      updateNode,
    ],
  );

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  );
}

/** Access tournament state and actions. Must be used within a provider. */
export function useTournament(): TournamentContextValue {
  const ctx = useContext(TournamentContext);
  if (!ctx) {
    throw new Error('useTournament must be used within a TournamentProvider');
  }
  return ctx;
}
